"use client";
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const BusinessReportVisuals = () => {
    const [activeStep, setActiveStep] = useState(0);

    // Auto-cycle through steps
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 5);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="w-full max-w-[500px] aspect-[4/3] bg-[#ffffff] rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.06)] border border-gray-100 relative overflow-hidden flex flex-col font-jakarta group ring-1 ring-gray-50/50">

            {/* Window Chrome */}
            <div className="h-10 border-b border-gray-50 bg-[#ffffff] flex items-center px-4 justify-between shrink-0 z-20">
                <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57] border border-[#e0443e]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e] border border-[#d89e24]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840] border border-[#1aab29]/30" />
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                        <span className="text-[9px] font-semibold text-gray-400 tracking-wide uppercase">AI Report Builder</span>
                    </div>
                </div>
                <div className="w-12" />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 relative overflow-hidden bg-[#ffffff] p-6 lg:p-8">
                <AnimatePresence mode="wait">
                    {activeStep === 0 && <Step1_Basics key="step1" />}
                    {activeStep === 1 && <Step2_Upload key="step2" />}
                    {activeStep === 2 && <Step3_QA key="step3" />}
                    {activeStep === 3 && <Step4_Theme key="step4" />}
                    {activeStep === 4 && <Step5_Edit key="step5" />}
                </AnimatePresence>

                {/* Progressive Step Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-50 flex">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex-1 relative h-full">
                            {activeStep === i && (
                                <motion.div
                                    className="absolute inset-0 bg-[#2563eb]"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 5, ease: "linear" }}
                                />
                            )}
                            {activeStep > i && <div className="absolute inset-0 bg-[#2563eb] opacity-30" />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Step Components ---

// Step 1: Share company basics
const Step1_Basics = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full justify-center gap-4"
    >
        <div className="space-y-3">
            <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                </div>
                <div className="flex-1">
                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Industry</div>
                    <div className="text-sm font-semibold text-gray-900">SaaS & Technology</div>
                </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                </div>
                <div className="flex-1">
                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Website</div>
                    <div className="text-sm font-semibold text-gray-900">acme-corp.com</div>
                </div>
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white"
                >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                </motion.div>
            </div>
        </div>
    </motion.div>
);

// Step 2: Upload Inputs
const Step2_Upload = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full justify-center"
    >
        <div className="w-full aspect-[2/1] border-2 border-dashed border-blue-200 rounded-2xl bg-blue-50/30 flex flex-col items-center justify-center gap-3 relative overflow-hidden">
            <motion.div
                initial={{ y: 0 }}
                animate={{ y: [-5, 5, -5] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className="w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center text-blue-500"
            >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            </motion.div>
            <div className="text-center">
                <div className="text-xs font-bold text-gray-900">Upload Financials</div>
                <div className="text-[10px] text-gray-500 mt-1">CSV, Excel, or PDF</div>
            </div>

            {/* Progress Bar Animation */}
            <motion.div
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ delay: 0.5, duration: 1.5 }}
                className="absolute bottom-0 left-0 h-1 bg-green-500"
            />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
                className="absolute inset-0 bg-white/80 flex items-center justify-center backdrop-blur-sm"
            >
                <div className="flex items-center gap-2 text-green-600 font-bold text-sm">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    <span>Data Extracted</span>
                </div>
            </motion.div>
        </div>
    </motion.div>
);

// Step 3: Guided Q&A
const Step3_QA = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-3"
    >
        {/* AI Question */}
        <motion.div
            initial={{ x: -10, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="flex items-start gap-2 max-w-[85%]"
        >
            <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-1">AI</div>
            <div className="bg-gray-100 rounded-2xl p-3 rounded-tl-none">
                <div className="text-xs text-gray-800 leading-snug">What is the primary goal of this Quarterly Report?</div>
            </div>
        </motion.div>

        {/* User Answer (Typing) */}
        <motion.div
            initial={{ x: 10, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex items-start gap-2 max-w-[85%] self-end flex-row-reverse"
        >
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-[10px] font-bold shrink-0 mt-1">You</div>
            <div className="bg-blue-600 rounded-2xl p-3 rounded-tr-none text-white">
                <div className="text-xs leading-snug">
                    Highlight Q3 revenue growth and new market entry...
                    <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ repeat: Infinity, duration: 0.8 }}
                        className="inline-block w-0.5 h-3 bg-white align-middle ml-1"
                    />
                </div>
            </div>
        </motion.div>
    </motion.div>
);

// Step 4: Theme Selection (Professional Report Styles)
const Step4_Theme = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-[#ffffff] relative overflow-hidden"
    >
        <div className="flex items-center justify-between mb-4 px-1">
            <div className="flex items-center gap-2">
                <div className="p-1 rounded-md bg-blue-50 text-blue-600"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg></div>
                <div className="text-xs font-bold text-gray-900">Choose Brand Style</div>
            </div>
        </div>

        <div className="grid grid-cols-2 gap-3 h-full pb-1">
            {/* Theme 1 - Corporate Blue (Selected) */}
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative rounded-xl border-2 border-blue-500 bg-white shadow-xl shadow-blue-900/5 group cursor-pointer overflow-hidden ring-4 ring-blue-50/50"
            >
                {/* Active Checkmark */}
                <div className="absolute top-2 right-2 z-20 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                </div>

                {/* Preview Content */}
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="h-10 bg-gradient-to-r from-blue-900 to-blue-800 p-2 flex items-center">
                        <div className="w-6 h-1.5 bg-white/20 rounded-full" />
                    </div>
                    {/* Body */}
                    <div className="p-3 flex-1 flex flex-col gap-2 bg-blue-50/10">
                        <div className="flex gap-2">
                            <div className="flex-1 space-y-1.5">
                                <div className="w-full h-1 bg-slate-200 rounded-full" />
                                <div className="w-2/3 h-1 bg-slate-100 rounded-full" />
                            </div>
                            <div className="w-6 h-6 rounded-full border-2 border-blue-100 bg-white" />
                        </div>
                        <div className="mt-auto h-8 w-full bg-blue-50 rounded border border-blue-100 relative overflow-hidden">
                            <div className="absolute bottom-0 left-0 w-full h-full flex items-end px-1 gap-0.5">
                                <div className="w-1/4 h-2/3 bg-blue-300 rounded-t-sm" />
                                <div className="w-1/4 h-full bg-blue-500 rounded-t-sm" />
                                <div className="w-1/4 h-1/2 bg-blue-200 rounded-t-sm" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-2 left-0 right-0 text-center">
                    <span className="text-[9px] font-bold text-blue-700 bg-white/80 backdrop-blur px-2 py-0.5 rounded-full border border-blue-100 shadow-sm">Executive Blue</span>
                </div>
            </motion.div>

            {/* Theme 2 - Minimal */}
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="relative rounded-xl border border-gray-100 bg-gray-50/50 grayscale opacity-60 hover:opacity-80 hover:scale-[1.02] transition-all cursor-pointer overflow-hidden"
            >
                <div className="h-full flex flex-col p-2">
                    <div className="h-2 w-1/3 bg-gray-300 rounded-full mb-3" />
                    <div className="space-y-2">
                        <div className="w-full h-1 bg-gray-200 rounded-full" />
                        <div className="w-5/6 h-1 bg-gray-200 rounded-full" />
                    </div>
                </div>
                <div className="absolute bottom-2 left-0 right-0 text-center">
                    <span className="text-[9px] font-bold text-gray-400">Minimal Gray</span>
                </div>
            </motion.div>
        </div>
    </motion.div>
);

// Step 5: Edit pages with AI (Enhanced UI)
const Step5_Edit = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-blue-50/20 relative"
    >
        {/* Editor Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 bg-white/80 border-b border-blue-100 backdrop-blur-sm">
            <div className="flex gap-2">
                <div className="px-2 py-1 rounded bg-blue-100/50 text-blue-600 text-[9px] font-bold flex items-center gap-1">
                    <span>Annual Report 2024</span>
                </div>
            </div>
            <div className="flex gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[8px] font-bold text-gray-400">SAVED</span>
            </div>
        </div>

        <div className="flex-1 p-3 overflow-hidden flex gap-3">
            {/* Main Page Canvas */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="flex-1 bg-white rounded-lg shadow-lg shadow-blue-900/5 border border-blue-100 p-4 relative"
            >
                {/* Header Section */}
                <div className="flex justify-between items-start mb-4">
                    <div className="w-1/2">
                        <div className="h-4 w-3/4 bg-slate-900 rounded mb-2" />
                        <div className="h-1.5 w-1/3 bg-blue-500 rounded-full" />
                    </div>
                    <div className="w-8 h-8 rounded-full border border-gray-100 bg-gray-50" />
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ delay: 0.2 }} className="h-1.5 bg-gray-100 rounded-full" />
                        <motion.div initial={{ width: 0 }} animate={{ width: "90%" }} transition={{ delay: 0.3 }} className="h-1.5 bg-gray-100 rounded-full" />
                        <motion.div initial={{ width: 0 }} animate={{ width: "95%" }} transition={{ delay: 0.4 }} className="h-1.5 bg-gray-100 rounded-full" />

                        <div className="mt-4 p-2 bg-blue-50/50 rounded border border-blue-100 relative">
                            <div className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[7px] font-bold px-1.5 py-0.5 rounded-full">AI SUMMARY</div>
                            <div className="space-y-1">
                                <div className="h-1 w-full bg-blue-200 rounded" />
                                <div className="h-1 w-5/6 bg-blue-200 rounded" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50/30 rounded-lg p-2 border border-blue-50 flex items-end justify-between gap-1">
                        <motion.div initial={{ height: 0 }} animate={{ height: "30%" }} transition={{ delay: 0.5 }} className="w-full bg-blue-300 rounded-t-sm" />
                        <motion.div initial={{ height: 0 }} animate={{ height: "50%" }} transition={{ delay: 0.6 }} className="w-full bg-blue-400 rounded-t-sm" />
                        <motion.div initial={{ height: 0 }} animate={{ height: "80%" }} transition={{ delay: 0.7 }} className="w-full bg-blue-600 rounded-t-sm shadow-sm" />
                        <motion.div initial={{ height: 0 }} animate={{ height: "45%" }} transition={{ delay: 0.8 }} className="w-full bg-blue-400 rounded-t-sm" />
                    </div>
                </div>

                {/* AI Interaction Overlay */}
                <motion.div
                    className="absolute bottom-4 right-4 z-20"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 1 }}
                >
                    <div className="flex items-center gap-2 bg-blue-600 pr-3 pl-1 py-1 rounded-full shadow-xl shadow-blue-500/20 text-white">
                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        </div>
                        <span className="text-[9px] font-bold tracking-wide">Analysing Data...</span>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    </motion.div>
);
